// Shared wording and formatting for the Debug tab: the three-state outcome, playbook names, times and durations.

export function asObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function asList(value) {
    return Array.isArray(value) ? value : [];
}

export function formatTimestamp(value) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function formatDuration(ms) {
    if (ms == null || ms === '') return '—';
    const value = Number(ms);
    if (!Number.isFinite(value)) return '—';
    if (value < 1000) return `${Math.round(value)} ms`;
    if (value < 60000) return `${(value / 1000).toFixed(1)} s`;
    // A debug can run for up to 24 hours, so minutes and hours stay readable ("1h 02m", not "3720.0 s").
    const totalSeconds = Math.round(value / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/** Elapsed time with a sign, as "+2m 05s" or "−45m"; used to place events relative to the request. */
export function formatOffset(ms) {
    if (ms == null || !Number.isFinite(ms)) return '';
    const sign = ms < 0 ? '−' : '+';
    const total = Math.round(Math.abs(ms) / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (hours > 0) return `${sign}${hours}h ${String(minutes).padStart(2, '0')}m`;
    if (minutes > 0) return `${sign}${minutes}m ${String(seconds).padStart(2, '0')}s`;
    return `${sign}${seconds}s`;
}

/** A configured span in seconds as people write it: "45 s", "30 min", "24 h", "1h 30m". */
export function formatSpan(seconds) {
    if (seconds == null || seconds === '') return '—';
    const value = Number(seconds);
    if (!Number.isFinite(value)) return '—';
    if (value < 60) return `${value} s`;
    if (value % 3600 === 0) return `${value / 3600} h`;
    if (value < 3600 && value % 60 === 0) return `${value / 60} min`;
    return formatDuration(value * 1000);
}

export function titleCase(value) {
    return String(value)
        .replace(/[-_]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

const STAGE_LABELS = {
    initial_telemetry_lookup: 'Initial telemetry lookup',
    additional_telemetry_lookup: 'Additional telemetry lookup',
    evidence_triage: 'Evidence selection',
    log_retrieval: 'Log retrieval',
    logtalk_execution: 'Log analysis',
    wbc_analysis: 'Telemetry analysis',
    merge_rca: 'Root cause summary',
    email: 'Result email',
};

/** A pipeline stage's display name: no internal identifiers or product names. */
export function stageLabel(name) {
    if (!name) return 'Stage';
    if (STAGE_LABELS[name]) return STAGE_LABELS[name];
    const words = String(name).replace(/[-_]+/g, ' ').trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A status reason for display. The service and worker write reasons from fixed text, artifact types, counts and
 * stage identifiers ("failed stages: merge_rca"); the identifiers are replaced with the same names the timeline uses.
 */
export function reasonLabel(reason) {
    if (!reason) return null;
    return Object.entries(STAGE_LABELS).reduce(
        (text, [id, label]) => text.replace(new RegExp(`\\b${id}\\b`, 'g'), label), String(reason));
}

// "default-crash-analysis" -> "Default Crash Analysis"
export function playbookLabel(value) {
    return value ? titleCase(value) : '—';
}

// A debug has succeeded, is in progress (for at most the service's maximum job duration), or has failed.
// The service sends `outcome`; the status map is the fallback for responses that predate it.
const STATUS_OUTCOME = {
    WAITING_FOR_LOGS: 'IN_PROGRESS',
    QUEUED: 'IN_PROGRESS',
    RUNNING: 'IN_PROGRESS',
    COMPLETED: 'SUCCESS',
    PARTIAL: 'SUCCESS',
    NOT_DEBUGGABLE: 'FAILED',
    FAILED: 'FAILED',
};

export const OUTCOME_LABELS = { SUCCESS: 'Success', IN_PROGRESS: 'In Progress', FAILED: 'Failure' };

export function outcomeOf(job) {
    return job.outcome || STATUS_OUTCOME[job.status] || 'IN_PROGRESS';
}

const FAILURE_REASON_LABELS = {
    NO_LOGS_FOUND: 'No logs found',
    MAX_JOB_DURATION: 'Timed out',
    WORKER_TIMEOUT: 'Worker timed out',
    WORKER_STALLED: 'Worker stalled',
    STAGING_FAILED: 'Log download failed',
    INSUFFICIENT_STAGING_SPACE: 'QDisk full',
    EMAIL_FAILED: 'Email send failure',
    ALL_ANALYSES_FAILED: 'All analyses failed',
    UNKNOWN_PLAYBOOK: 'Unknown playbook',
    WORKFLOW_ERROR: 'Workflow error',
    LOGTALK_FAILED: 'Log analysis failed',
    LOGTALK_TIMEOUT: 'Log analysis timed out',
    LOGTALK_CATEGORY_MISSING: 'Log analysis category missing',
    LOGTALK_BAD_RESPONSE: 'Log analysis bad response',
    LOGTALK_HTTP_ERROR: 'Log analysis HTTP error',
    TELEMETRY_LOOKUP_FAILED: 'Telemetry lookup failed',
    TELEMETRY_HTTP_ERROR: 'Telemetry HTTP error',
    TELEMETRY_TIMEOUT: 'Telemetry timed out',
    STEP_ERROR: 'Pipeline step error',
    UNKNOWN: 'Unknown',
};

export function failureReasonLabel(code) {
    return code ? FAILURE_REASON_LABELS[code] || titleCase(code) : null;
}

// Why a log-analysis step was skipped, by the stable skip code the worker reports (never by its free-text detail).
// NOT_DEBUGGABLE jobs are excluded server-side (they are "No logs found" in the failure chart), so a skip here means
// the debug went on without log analysis.
const SKIP_CODE_LABELS = {
    NO_LOGS: 'No logs to analyze',
    NOT_REQUESTED: 'Not requested',
    ALREADY_DONE: 'Already done',
    NO_ANALYSIS_OUTPUT: 'No analysis output',
    SINGLE_ANALYSIS: 'Single analysis',
    INTERRUPTED: 'Interrupted (retried)',
    REUSED: 'Reused from an earlier attempt',
    UNKNOWN: 'Unknown (older debug)',
};

export function skipCodeLabel(code) {
    return code ? SKIP_CODE_LABELS[code] || titleCase(code) : SKIP_CODE_LABELS.UNKNOWN;
}

const SOURCE_TYPE_LABELS = { AUTOMATED: 'Automated', MANUAL: 'Manual', UNSPECIFIED: 'Not specified' };

export function sourceTypeLabel(type) {
    return SOURCE_TYPE_LABELS[type] || (type ? titleCase(type) : SOURCE_TYPE_LABELS.UNSPECIFIED);
}

/** "Automated · health-manager" from the caller-declared source; null when the caller did not send one. */
export function sourceLabel(source) {
    if (!source || typeof source !== 'object' || !source.system) return null;
    return `${sourceTypeLabel(source.type)} · ${source.system}`;
}

// Categorical slots in fixed order (validated: CVD and normal-vision separation pass on the light surface). Each chart
// pins its known codes to a slot so a code keeps its color whatever its rank; unknown codes take the unused slots in
// this order, and anything past the last slot folds into a neutral "Other" rather than cycling a hue.
export const CATEGORICAL_SLOTS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
export const NEUTRAL_COLOR = '#8b8b86';
export const OTHER_CODE = '__OTHER__';

/**
 * Pie segments for {code: count}: [{code, value, color}], one distinct color each. palette maps known codes to a slot
 * color (or NEUTRAL_COLOR for UNKNOWN-like codes); zero counts are dropped.
 */
export function pieSegments(counts, palette) {
    const entries = Object.entries(counts || {}).map(([code, value]) => [code, Number(value) || 0]).filter(([, v]) => v > 0);
    const used = new Set(entries.map(([code]) => palette[code]).filter(Boolean));
    const spare = CATEGORICAL_SLOTS.filter((color) => !used.has(color));
    const segments = [];
    let other = 0;
    entries.forEach(([code, value]) => {
        const color = palette[code] || spare.shift();
        if (color) segments.push({ code, value, color });
        else other += value;
    });
    if (other > 0) segments.push({ code: OTHER_CODE, value: other, color: NEUTRAL_COLOR });
    return segments;
}

const PHASE_NOTES = {
    WAITING_FOR_LOGS: 'waiting for logs',
    QUEUED: 'queued for analysis',
    RUNNING: 'analyzing',
};

/** The short qualifier shown next to the outcome: why it failed, what it is waiting for, or that it was degraded. */
export function outcomeNote(job) {
    const outcome = outcomeOf(job);
    if (outcome === 'IN_PROGRESS') return PHASE_NOTES[job.status] || null;
    if (outcome === 'SUCCESS') return job.degraded || job.status === 'PARTIAL' ? 'partial analysis' : null;
    const code = job.failureReason || (job.status === 'NOT_DEBUGGABLE' ? 'NO_LOGS_FOUND' : job.error?.code);
    return failureReasonLabel(code);
}

// What a RUNNING job is doing, as the worker's heartbeat reports it.
const EXECUTION_PHASE_LABELS = {
    ACTIVE: 'Working',
    WAITING_FOR_LOGTALK: 'Waiting for log analysis',
    WAITING_FOR_CAPACITY: 'Waiting for worker capacity',
};

export function executionPhaseLabel(phase) {
    return phase ? EXECUTION_PHASE_LABELS[phase] || titleCase(phase) : null;
}

// Why the Log Locator queued a job when it did (pickup.resolution).
const PICKUP_RESOLUTION_LABELS = {
    WAITING: 'Still waiting for logs',
    QUEUED_WINDOW_CLOSED: 'Queued after the log window closed',
    QUEUED_LIMITS_REACHED: 'Queued early: every log type had its full count',
    QUEUED_DEADLINE: 'Queued at the deadline with the logs found',
};

export function pickupResolutionLabel(resolution) {
    return resolution ? PICKUP_RESOLUTION_LABELS[resolution] || titleCase(resolution) : null;
}
